import { FeatureFlagsKeysEnum, GetSubscriptionDto, PermissionsEnum } from '@novu/shared';
import { SVGProps } from 'react';
import {
  RiBarChartBoxLine,
  RiBuildingLine,
  RiCodeSSlashLine,
  RiDatabase2Line,
  RiDiscussLine,
  RiGroup2Line,
  RiKey2Line,
  RiLayout5Line,
  RiLineChartLine,
  RiRobot2Line,
  RiRouteFill,
  RiSettings4Line,
  RiSignalTowerLine,
  RiStore3Line,
  RiTranslate2,
  RiUserAddLine,
} from 'react-icons/ri';
import { useNavigate } from 'react-router-dom';
import { useUsageLimitsView } from '@/components/billing/usage-limits/use-usage-limits-view';
import { SidebarContent } from '@/components/side-navigation/sidebar';
import { useEnvironment } from '@/context/environment/hooks';
import { useLocalMode } from '@/context/local-mode';
import { useAreConversationalAgentsAvailable } from '@/hooks/use-are-conversational-agents-available';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useHasPermission } from '@/hooks/use-has-permission';
import { Protect } from '@/utils/protect';
import { buildRoute, ROUTES } from '@/utils/routes';
import { IS_SELF_HOSTED, IS_SELF_HOSTED_CE } from '../../config';
import { useFetchSubscription } from '../../hooks/use-fetch-subscription';
import { ChangelogStack } from './changelog-cards';
import { EnvironmentDropdown, LOCAL_ENVIRONMENT_VALUE } from './environment-dropdown';
import { FreeTrialCard } from './free-trial-card';
import { HomeMenuItem } from './getting-started-menu-item';
import { NavigationGroup } from './navigation-group';
import { NavigationLink } from './navigation-link';
import { OrganizationDropdown } from './organization-dropdown';
import { getSidebarPlanCardVariant, type SidebarPlanCardVariant } from './sidebar-plan-card-variant';
import { FreeUsageCard, PausedUsageCard } from './usage-card';

function MailAiLineIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M20.7134 8.12811L20.4668 8.69379C20.2864 9.10792 19.7136 9.10792 19.5331 8.69379L19.2866 8.12811C18.8471 7.11947 18.0555 6.31641 17.0677 5.87708L16.308 5.53922C15.8973 5.35653 15.8973 4.75881 16.308 4.57612L17.0252 4.25714C18.0384 3.80651 18.8442 2.97373 19.2761 1.93083L19.5293 1.31953C19.7058 0.893489 20.2942 0.893489 20.4706 1.31953L20.7238 1.93083C21.1558 2.97373 21.9616 3.80651 22.9748 4.25714L23.6919 4.57612C24.1027 4.75881 24.1027 5.35653 23.6919 5.53922L22.9323 5.87708C21.9445 6.31641 21.1529 7.11947 20.7134 8.12811ZM2 4C2 3.44772 2.44772 3 3 3H14V5H4.5052L12 11.662L16.3981 7.75259L17.7269 9.24741L12 14.338L4 7.22684V19H20V11H22V20C22 20.5523 21.5523 21 21 21H3C2.44772 21 2 20.5523 2 20V4Z" />
    </svg>
  );
}

function buildEnvironmentRoute(route: string, environmentSlug: string | undefined): string | undefined {
  return environmentSlug ? buildRoute(route, { environmentSlug }) : undefined;
}

type SidebarPlanCardProps = {
  variant: SidebarPlanCardVariant;
  subscription: GetSubscriptionDto;
  daysLeft: number;
  canEditUsageLimits: boolean;
};

function SidebarPlanCard({ variant, subscription, daysLeft, canEditUsageLimits }: SidebarPlanCardProps) {
  switch (variant) {
    case 'trial':
      return <FreeTrialCard subscription={subscription} daysLeft={daysLeft} />;
    case 'free_usage':
      return <FreeUsageCard subscription={subscription} />;
    case 'paused_usage':
      return <PausedUsageCard subscription={subscription} canEditUsageLimits={canEditUsageLimits} />;
    default: {
      const exhaustiveCheck: never = variant;

      return exhaustiveCheck;
    }
  }
}

type BottomNavigationProps = {
  isLoadingSubscription: boolean;
  subscription: GetSubscriptionDto | undefined;
  daysLeft: number;
};

const BottomSection = ({ isLoadingSubscription, subscription, daysLeft }: BottomNavigationProps) => {
  const usageLimits = useUsageLimitsView();

  if (IS_SELF_HOSTED) {
    return (
      <div className="relative mt-auto gap-8 pt-4">
        <HomeMenuItem />
      </div>
    );
  }

  const planCardVariant = getSidebarPlanCardVariant(subscription, usageLimits?.pausedPlan ?? null);

  return (
    <div className="relative mt-auto gap-8 pt-4">
      {planCardVariant !== 'trial' && !isLoadingSubscription && <ChangelogStack />}
      {subscription && planCardVariant && (
        <SidebarPlanCard
          variant={planCardVariant}
          subscription={subscription}
          daysLeft={daysLeft}
          canEditUsageLimits={usageLimits?.canEdit ?? false}
        />
      )}
      <NavigationGroup>
        <NavigationLink to={ROUTES.SETTINGS_TEAM}>
          <RiUserAddLine className="size-4" />
          <span>Invite teammates</span>
        </NavigationLink>
        <HomeMenuItem />
      </NavigationGroup>
    </div>
  );
};

export const LegacySideNavigation = () => {
  const { subscription, daysLeft, isLoading: isLoadingSubscription } = useFetchSubscription();
  const isWebhooksManagementEnabled = useFeatureFlag(FeatureFlagsKeysEnum.IS_WEBHOOKS_MANAGEMENT_ENABLED);
  const isDomainsPageEnabled = useFeatureFlag(FeatureFlagsKeysEnum.IS_DOMAINS_PAGE_ENABLED);
  const isHttpLogsPageEnabled = useFeatureFlag(FeatureFlagsKeysEnum.IS_HTTP_LOGS_PAGE_ENABLED, false);
  const isAnalyticsPageEnabled = useFeatureFlag(FeatureFlagsKeysEnum.IS_ANALYTICS_PAGE_ENABLED, false);
  const showAgents = useAreConversationalAgentsAvailable();

  const { currentEnvironment, environments, switchEnvironment } = useEnvironment();
  const environmentSlug = currentEnvironment?.slug;
  const localMode = useLocalMode();
  const has = useHasPermission();
  const navigate = useNavigate();

  // Local mode makes the API sign requests to the caller's tunnel with the
  // environment key, so it is gated on BRIDGE_WRITE (see bridge.controller.ts).
  // Hide the picker entry from members who would only hit a 403.
  const canUseLocalMode = has({ permission: PermissionsEnum.BRIDGE_WRITE });
  const showLocalEntry = canUseLocalMode && Boolean(localMode.session);

  const onEnvironmentChange = (value: string) => {
    if (value === LOCAL_ENVIRONMENT_VALUE) {
      if (localMode.session) {
        navigate(buildRoute(ROUTES.LOCAL_WORKFLOWS, { environmentSlug: localMode.session.environmentSlug }));
      }

      return;
    }

    const environment = environments?.find((env) => env.name === value);

    if (localMode.isLocalRoute) {
      // Leaving local mode: land on the regular workflows list of the target
      // environment instead of rewriting the /local/* path.
      navigate(buildRoute(ROUTES.WORKFLOWS, { environmentSlug: environment?.slug ?? '' }));

      return;
    }

    switchEnvironment(environment?.slug);
  };

  return (
    <aside className="relative flex h-full w-[275px] shrink-0 flex-col">
      <SidebarContent className="h-full">
        <OrganizationDropdown />
        <EnvironmentDropdown
          currentEnvironment={currentEnvironment}
          data={environments}
          onChange={onEnvironmentChange}
          localEntry={showLocalEntry ? { status: localMode.healthStatus } : undefined}
          isLocalSelected={localMode.isLocalRoute}
        />
        <nav className="flex h-full flex-1 flex-col overflow-auto">
          <div className="flex flex-col gap-4">
            {showAgents && (
              <NavigationGroup label="Agents">
                <NavigationLink to={buildEnvironmentRoute(ROUTES.AGENTS, environmentSlug)}>
                  <RiRobot2Line className="size-4" />
                  <span>Agents</span>
                </NavigationLink>
              </NavigationGroup>
            )}

            <NavigationGroup label="Notifications">
              <Protect permission={PermissionsEnum.WORKFLOW_READ}>
                <NavigationLink
                  to={buildEnvironmentRoute(
                    // Workflows is the one resource the Local pseudo-environment
                    // overlays — keep the nav inside local mode instead of
                    // silently dropping the user back to the synced dev list.
                    localMode.isLocalRoute ? ROUTES.LOCAL_WORKFLOWS : ROUTES.WORKFLOWS,
                    environmentSlug
                  )}
                >
                  <RiRouteFill className="size-4" />
                  <span>Workflows</span>
                </NavigationLink>
              </Protect>
              <Protect permission={PermissionsEnum.WORKFLOW_READ}>
                <NavigationLink to={buildEnvironmentRoute(ROUTES.LAYOUTS, environmentSlug)}>
                  <RiLayout5Line className="size-4" />
                  <span>Layouts</span>
                </NavigationLink>
              </Protect>
              <NavigationLink to={buildEnvironmentRoute(ROUTES.TRANSLATIONS, environmentSlug)}>
                <RiTranslate2 className="size-4" />
                <span>Translations</span>
              </NavigationLink>
            </NavigationGroup>
            <NavigationGroup label="Data">
              <Protect permission={PermissionsEnum.SUBSCRIBER_READ}>
                <NavigationLink to={buildEnvironmentRoute(ROUTES.SUBSCRIBERS, environmentSlug)}>
                  <RiGroup2Line className="size-4" />
                  <span>Subscribers</span>
                </NavigationLink>
              </Protect>
              <Protect permission={PermissionsEnum.WORKFLOW_READ}>
                <NavigationLink to={buildEnvironmentRoute(ROUTES.CONTEXTS, environmentSlug)}>
                  <RiBuildingLine className="size-4" />
                  <span>Contexts</span>
                </NavigationLink>
              </Protect>
              <Protect permission={PermissionsEnum.TOPIC_READ}>
                <NavigationLink to={buildEnvironmentRoute(ROUTES.TOPICS, environmentSlug)}>
                  <RiDiscussLine className="size-4" />
                  <span>Topics</span>
                </NavigationLink>
              </Protect>
            </NavigationGroup>
            <Protect permission={PermissionsEnum.NOTIFICATION_READ}>
              <NavigationGroup label="Monitor">
                <Protect permission={PermissionsEnum.NOTIFICATION_READ}>
                  <NavigationLink
                    to={buildEnvironmentRoute(
                      isHttpLogsPageEnabled ? ROUTES.ACTIVITY_WORKFLOW_RUNS : ROUTES.ACTIVITY_FEED,
                      environmentSlug
                    )}
                    matchPaths={
                      environmentSlug
                        ? [
                            buildRoute(ROUTES.ACTIVITY_FEED, { environmentSlug }),
                            buildRoute(ROUTES.ACTIVITY_WORKFLOW_RUNS, { environmentSlug }),
                            buildRoute(ROUTES.ACTIVITY_REQUESTS, { environmentSlug }),
                            buildRoute(ROUTES.ACTIVITY_CONVERSATIONS, { environmentSlug }),
                          ]
                        : undefined
                    }
                  >
                    <RiBarChartBoxLine className="size-4" />
                    <span>Activity</span>
                  </NavigationLink>
                </Protect>
                {isAnalyticsPageEnabled && (
                  <Protect permission={PermissionsEnum.NOTIFICATION_READ}>
                    <NavigationLink to={buildEnvironmentRoute(ROUTES.ANALYTICS, environmentSlug)}>
                      <RiLineChartLine className="size-4" />
                      <span>Usage</span>
                    </NavigationLink>
                  </Protect>
                )}
              </NavigationGroup>
            </Protect>
            <Protect
              condition={(has) =>
                has({ permission: PermissionsEnum.API_KEY_READ }) ||
                has({ permission: PermissionsEnum.WEBHOOK_READ }) ||
                has({ permission: PermissionsEnum.WEBHOOK_WRITE })
              }
            >
              <NavigationGroup label="Developer">
                <Protect permission={PermissionsEnum.API_KEY_READ}>
                  <NavigationLink to={buildEnvironmentRoute(ROUTES.API_KEYS, environmentSlug)}>
                    <RiKey2Line className="size-4" />
                    <span>API Keys</span>
                  </NavigationLink>
                </Protect>
                {isWebhooksManagementEnabled && (
                  <Protect
                    condition={(has) =>
                      has({ permission: PermissionsEnum.WEBHOOK_READ }) ||
                      has({ permission: PermissionsEnum.WEBHOOK_WRITE })
                    }
                  >
                    <NavigationLink to={buildEnvironmentRoute(ROUTES.WEBHOOKS, environmentSlug)}>
                      <RiSignalTowerLine className="size-4" />
                      <span className="flex items-center gap-2">Webhooks</span>
                    </NavigationLink>
                  </Protect>
                )}
                {isDomainsPageEnabled && !IS_SELF_HOSTED_CE && (
                  <NavigationLink to={buildEnvironmentRoute(ROUTES.DOMAINS, environmentSlug)}>
                    <MailAiLineIcon className="size-4" />
                    <span>Inbound Email</span>
                  </NavigationLink>
                )}
                <NavigationLink to={buildEnvironmentRoute(ROUTES.ENVIRONMENTS, environmentSlug)}>
                  <RiDatabase2Line className="size-4" />
                  <span>Environments</span>
                </NavigationLink>
                <NavigationLink to={buildEnvironmentRoute(ROUTES.VARIABLES, environmentSlug)}>
                  <RiCodeSSlashLine className="size-4" />
                  <span>Variables</span>
                </NavigationLink>
              </NavigationGroup>
            </Protect>
            <Protect
              condition={(has) =>
                has({ permission: PermissionsEnum.INTEGRATION_READ }) || IS_SELF_HOSTED || !IS_SELF_HOSTED_CE
              }
            >
              <NavigationGroup label="Platform">
                <Protect permission={PermissionsEnum.INTEGRATION_READ}>
                  <NavigationLink to={buildEnvironmentRoute(ROUTES.INTEGRATIONS, environmentSlug)}>
                    <RiStore3Line className="size-4" />
                    <span>Integration Store</span>
                  </NavigationLink>
                </Protect>
                {/* ReNovu: Settings menu accessible for self-hosted deployments */}
                <NavigationLink to={ROUTES.SETTINGS}>
                  <RiSettings4Line className="size-4" />
                  <span>Settings</span>
                </NavigationLink>
              </NavigationGroup>
            </Protect>
          </div>

          <BottomSection
            isLoadingSubscription={isLoadingSubscription}
            subscription={subscription}
            daysLeft={daysLeft}
          />
        </nav>
      </SidebarContent>
    </aside>
  );
};
